import React from 'react';
import { useFeature } from '../../modules/entitlements';
import { useAuthContext } from '../../modules/auth';
import { UpgradePrompt } from './UpgradePrompt';
import { useSettingsDialog } from '../settings/useSettingsDialog';

interface RequireFeatureProps {
  feature: string;
  children: React.ReactNode;
  // Custom fallback — if omitted, shows UpgradePrompt
  fallback?: React.ReactNode;
  // Passed to the default UpgradePrompt
  requiredPlan?: 'STARTER' | 'GROWTH' | 'PRO';
  // If true, renders nothing instead of UpgradePrompt when not entitled
  silent?: boolean;
}

/** SOFT gate — UI only; the backend ALWAYS enforces via FeatureGuard. `silent` renders nothing when locked. */
export function RequireFeature({
  feature,
  children,
  fallback,
  requiredPlan,
  silent = false,
}: RequireFeatureProps) {
  const { open: openSettings } = useSettingsDialog();
  const {
    state: { accessFetched },
  } = useAuthContext();
  const entitled = useFeature(feature);

  // Entitlements load fire-and-forget after auth (the same `accessFetched` latch
  // the permission gate waits on). Until they settle, `useFeature` reads false
  // for every key, so rendering the not-entitled branch here would flash the
  // UpgradePrompt over an entitled org on a hard refresh. Render nothing until
  // access settles; `accessFetched` always flips true (even on request failure),
  // so this can never hang.
  if (!accessFetched) return null;

  if (!entitled) {
    if (silent) return null;
    if (fallback !== undefined) return <>{fallback}</>;
    return (
      <UpgradePrompt
        featureKey={feature}
        requiredPlan={requiredPlan}
        onUpgrade={() => openSettings('billing')}
      />
    );
  }

  return <>{children}</>;
}
